// Pieces shared by the audit and booking detail pages.
import React from 'react';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaCalendarAlt, FaClock, FaCheck } from 'react-icons/fa';
import { Card, Badge, formatMoney, formatDate, initials } from './index';

// Renders a link in the operations panel, plain text where the target page does not exist.
const MaybeLink = ({ to, className, children }) =>
  to ? <Link to={to} className={className}>{children}</Link> : <span className={className}>{children}</span>;

// Status of one vendor's assignment on a job.
const ASSIGNMENT = {
  '-2': ['Withdrawn', 'gray'],
  '-1': ['Declined', 'red'],
  0: ['Awaiting response', 'amber'],
  1: ['Accepted', 'blue'],
  2: ['Employee assigned', 'violet'],
  3: ['Work complete', 'green'],
};
export const assignmentStatus = (code) => {
  const [label, tone] = ASSIGNMENT[code] || ['Unknown', 'gray'];
  return { label, tone };
};

export const formatAddress = (a) =>
  a ? [a.line_1, a.line_2, a.city, a.state, a.postal_code].filter(Boolean).join(', ') : '—';

export const ServiceSummary = ({ service, date, time, address, note, links = true }) => (
  <Card flush>
    <div className="job-hero">
      {service.images && service.images[0] && <img src={service.images[0]} alt="" />}
      <div className="job-hero-body">
        <span className="eyebrow">{service.category ? service.category.name : ''}</span>
        <h2>
          <MaybeLink to={links && `/manage-services/view-service/${service.id}`}>{service.service_name}</MaybeLink>
        </h2>
        <span className="mono muted">{service.service_id}</span>
      </div>
    </div>
    <div className="card-body">
      <ul className="facts">
        <li><FaCalendarAlt /> <span><strong>{date || '—'}</strong><em>Date</em></span></li>
        <li><FaClock /> <span><strong>{time ? time.slice(0, 5) : '—'}</strong><em>Time</em></span></li>
        <li className="wide"><FaMapMarkerAlt /> <span><strong>{formatAddress(address)}</strong><em>Address</em></span></li>
      </ul>
      {note && <p className="note">“{note}”</p>}
    </div>
  </Card>
);

export const CustomerCard = ({ user, links = true }) => (
  <Card title="Customer">
    <div className="row" style={{ flexWrap: 'nowrap', gap: 12 }}>
      <div className="avatar lg">{initials(user.username)}</div>
      <div style={{ minWidth: 0 }}>
        <MaybeLink to={links && `/manage-users/user/${user.id}`} className="strong">{user.username}</MaybeLink>
        <div className="small muted">{user.phone_number}</div>
        <div className="small muted" style={{ wordBreak: 'break-all' }}>{user.email}</div>
      </div>
    </div>
  </Card>
);

export const VendorHistory = ({ assignments, links = true, title = 'Vendor assignments' }) => (
  <Card title={title} hint={assignments.length ? undefined : 'No vendor assigned yet.'}>
    {assignments.length > 0 && (
      <ul className="timeline">
        {[...assignments].reverse().map((item, i) => {
          const s = assignmentStatus(item.status_code);
          const latest = i === 0;
          return (
            <li key={i} className={item.status_code < 0 ? 'failed' : latest ? 'current' : 'done'}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="t-title">
                  <MaybeLink to={links && `/manage-vendors/vendor/${item.vendor.id}`}>{item.vendor.company_name}</MaybeLink>
                </span>
                <Badge tone={s.tone}>{s.label}</Badge>
              </div>
              <div className="t-meta">
                {item.vendor.representative_name} · {item.vendor.company_phone}
                {item.remarks ? ` · “${item.remarks}”` : ''}
              </div>
              {item.staff_assignment_list && item.staff_assignment_list.length > 0 && (
                <div className="staff-pills">
                  {item.staff_assignment_list.map((st) => (
                    <MaybeLink key={st.id} to={links && `/staff/${st.id}`} className="staff-pill">
                      <span className="avatar" style={{ width: 22, height: 22, fontSize: 10 }}>{initials(st.name)}</span>
                      {st.name} · {st.phone_number}
                    </MaybeLink>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    )}
  </Card>
);

// Findings recorded by field staff: one block per requirement, the ticked options highlighted.
export const Findings = ({ title, items, remarkKey = 'remark', onlySelected }) => {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <Card title={title}>
      <div className="stack" style={{ gap: 16 }}>
        {items.map((req, i) => {
          const details = Array.isArray(req.details) ? req.details : [];
          const shown = onlySelected ? details.filter((d) => d.selected) : details;
          return (
            <div key={i}>
              <div className="label" style={{ marginBottom: 8 }}>{req.requirement}</div>
              <div className="finding-list">
                {shown.map((d, j) => (
                  <div key={j} className={`finding${d.selected ? ' on' : ''}`}>
                    <span className="tick">{d.selected && <FaCheck />}</span>
                    <div className="finding-main">
                      <strong>{d.option}</strong>
                      {(d[remarkKey] || d.remark) && <span className="small muted">{d[remarkKey] || d.remark}</span>}
                    </div>
                    <div className="finding-side">
                      <strong>{formatMoney(d.cost)}</strong>
                      <span className="small muted">{d.duration}</span>
                    </div>
                  </div>
                ))}
                {!shown.length && <span className="muted small">Nothing selected.</span>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export const Meta = ({ created }) => (
  <span className="small muted">Created {formatDate(created)}</span>
);
